import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

interface LegalConsentModalProps {
  isOpen: boolean;
  onAgree: () => void;
  onDisagree: () => void;
  isReconsent?: boolean;
}

const LegalConsentModal: React.FC<LegalConsentModalProps> = ({
  isOpen,
  onAgree,
  onDisagree,
  isReconsent = false,
}) => {
  const [agreed, setAgreed] = useState(false);

  const handleAgree = () => {
    if (agreed) {
      onAgree();
      setAgreed(false);
    }
  };

  const handleDisagree = () => {
    setAgreed(false);
    onDisagree();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDisagree()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isReconsent ? 'Updated Terms & Privacy Policy' : 'Terms & Privacy Policy'}
          </DialogTitle>
          <DialogDescription>
            {isReconsent
              ? 'Our Terms of Service and Privacy Policy have been updated. Please review and agree to continue using our service.'
              : 'Please review and agree to our Terms of Service and Privacy Policy to create an account.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Single checkbox for both policies */}
          <div className="flex items-start space-x-3">
            <Checkbox
              checked={agreed}
              onCheckedChange={(checked: boolean | string) => setAgreed(!!checked)}
              className="mt-1"
            />
            <div className="flex flex-col">
              <label
                htmlFor="consent"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                I agree to the{' '}
                <Link href="/terms" className="text-primary hover:underline" target="_blank">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link href="/privacy" className="text-primary hover:underline" target="_blank">
                  Privacy Policy
                </Link>
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                I have read and agree to the terms governing my use of the service and consent to the collection and processing of my personal data
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleDisagree}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAgree}
            disabled={!agreed}
            className="w-full sm:w-auto"
          >
            Agree & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LegalConsentModal;
